import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Grid,
    Card,
    CardContent,
    Link,
    Stack,
    IconButton,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import {
    Email as EmailIcon,
    LinkedIn as LinkedInIcon,
    Phone as PhoneIcon,
    Description as DescriptionIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Candidate } from '../types/jobVacancy';
import { candidateService } from '../services/candidateService';
import { jobVacancyService } from '../services/jobVacancyService';

export const TalentPool: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const candidateId = searchParams.get('candidate');

    useEffect(() => {
        loadCandidates();
    }, []);

    const loadCandidates = async () => {
        try {
            setLoading(true);
            const data = await candidateService.getAllCandidates();
            setCandidates(data);
        } catch (error) {
            enqueueSnackbar('Erro ao carregar candidatos', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredCandidates = candidates.filter(candidate => {
        const searchLower = searchTerm.toLowerCase();
        return (
            candidate.name.toLowerCase().includes(searchLower) ||
            candidate.email.toLowerCase().includes(searchLower) ||
            (candidate.phone && candidate.phone.includes(searchTerm))
        );
    });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Banco de Talentos
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <TextField
                        fullWidth
                        placeholder="Buscar candidatos por nome, email ou telefone"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        sx={{ maxWidth: 600 }}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {filteredCandidates.map((candidate) => (
                            <Grid item xs={12} md={6} lg={4} key={candidate.id}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        bgcolor: candidateId === candidate.id.toString() ? 'primary.light' : 'background.paper',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {candidate.name}
                                        </Typography>

                                        <Stack spacing={1}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <EmailIcon fontSize="small" />
                                                <Link href={`mailto:${candidate.email}`}>
                                                    {candidate.email}
                                                </Link>
                                            </Box>

                                            {candidate.phone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PhoneIcon fontSize="small" />
                                                    <Typography>{candidate.phone}</Typography>
                                                </Box>
                                            )}

                                            {candidate.linkedin_url && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinkedInIcon fontSize="small" />
                                                    <Link href={candidate.linkedin_url} target="_blank">
                                                        LinkedIn
                                                    </Link>
                                                </Box>
                                            )}

                                            {candidate.resume_url && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <DescriptionIcon fontSize="small" />
                                                    <Link component="button" type="button" onClick={() => jobVacancyService.openResume(candidate.resume_url!).catch(() => alert('Não foi possível abrir o currículo'))}>
                                                        Currículo
                                                    </Link>
                                                </Box>
                                            )}

                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Cadastrado em: {formatDate(candidate.created_at)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}

                        {filteredCandidates.length === 0 && !loading && (
                            <Grid item xs={12}>
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <Typography color="text.secondary">
                                        Nenhum candidato encontrado
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Box>
        </Container>
    );
}; 